import { axiosClient } from '@/libs/axios-client';
import {
  extractFileNameFromDisposition,
  saveBlobAsFile,
} from '../utils/exportUtils';

/**
 * Preview yearly holiday days update (dry run).
 * Shows what would happen if addYearlyHolidayDays is executed.
 * @param {number} year - The year for which to preview holiday days update.
 * @returns {Promise<Object>} A promise that resolves with the preview results.
 * @throws Will throw an error if the request fails.
 */
export const previewYearlyHolidayDays = async (year) => {
  try {
    const response = await axiosClient.get('/admin/holiday/preview-yearly-days', {
      params: { year },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to preview yearly holiday days:', error);
    throw error;
  }
};

/**
 * Add yearly holiday days to all active employees.
 * @param {number} year - The year for which to add holiday days.
 * @returns {Promise<Object>} A promise that resolves with the result.
 * @throws Will throw an error if the request fails.
 */
export const addYearlyHolidayDays = async (year) => {
  try {
    const response = await axiosClient.post('/admin/holiday/add-yearly-days', { year });
    return response.data;
  } catch (error) {
    console.error('Failed to add yearly holiday days:', error);
    throw error;
  }
};

/**
 * Preview expiry of old (carry-over) holiday days past the cutoff (dry run).
 * @param {number} year - The year whose carry-over expires.
 * @param {boolean} [force] - Ignore the "edge date has passed" gate (preview only).
 * @returns {Promise<Object>} A promise that resolves with the preview results.
 * @throws Will throw an error if the request fails.
 */
export const previewExpireOldHolidayDays = async ({
  year,
  force = false,
  filters = {},
  page = 1,
  pageSize = 20,
} = {}) => {
  try {
    const response = await axiosClient.get('/admin/holiday/preview-expire-old-days', {
      params: { year, force, page, pageSize, filters: JSON.stringify(filters) },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to preview expire old holiday days:', error);
    throw error;
  }
};

/**
 * Expire old (carry-over) holiday days past the cutoff (persists changes).
 * @param {Object} params
 * @param {number} params.year - The year whose carry-over expires.
 * @param {boolean} [params.force] - Ignore the "edge date has passed" gate.
 * @param {Object} [params.filters] - { companies, actions, types, cutoffs }.
 * @returns {Promise<Object>} A promise that resolves with the result.
 * @throws Will throw an error if the request fails.
 */
export const expireOldHolidayDays = async ({ year, force = false, filters = {} } = {}) => {
  try {
    const response = await axiosClient.post('/admin/holiday/expire-old-days', {
      year,
      force,
      filters,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to expire old holiday days:', error);
    throw error;
  }
};

/**
 * Export the filtered old-holiday expiry rows as an .xlsx file (server rebuilds
 * the rows from the same filters). Triggers a browser download.
 * @param {Object} params
 * @param {number} params.year - The year whose carry-over is being expired.
 * @param {boolean} [params.force] - Ignore the "edge date has passed" gate.
 * @param {Object} [params.filters] - { companies, actions, types, cutoffs }.
 * @returns {Promise<void>}
 * @throws Will throw an error if the request fails.
 */
export const exportExpireOldHolidayDays = async ({ year, force = false, filters = {} } = {}) => {
  try {
    const response = await axiosClient.post(
      '/admin/holiday/expire-old-days/export',
      { year, force, filters },
      { responseType: 'blob' }
    );
    const contentType =
      response.headers['content-type'] ||
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const fileName =
      extractFileNameFromDisposition(response.headers['content-disposition']) ||
      `expire-old-holiday-${year}.xlsx`;
    saveBlobAsFile(response.data, contentType, fileName);
  } catch (error) {
    console.error('Failed to export expire old holiday days:', error);
    throw error;
  }
};

/**
 * Reads the instance-wide security switches (tech admin only).
 *
 * @returns {Promise<Object>} A promise resolving with the current settings.
 * @throws Will throw an error if the request fails.
 */
export const getSecuritySettings = async () => {
  const response = await axiosClient.get('/admin/security-settings');

  return response.data;
};

/**
 * Updates the instance-wide security switches (tech admin only).
 *
 * @param {Object} payload - Fields to change.
 * @param {boolean} [payload.internalMfaRequired] - Require a sign-in code for
 *   HLB's own accounts.
 * @returns {Promise<Object>} A promise resolving with the stored settings.
 * @throws Will throw an error if the request fails.
 */
export const updateSecuritySettings = async (payload) => {
  const response = await axiosClient.put('/admin/security-settings', payload);

  return response.data;
};

const AdminService = {
  previewYearlyHolidayDays,
  addYearlyHolidayDays,
  previewExpireOldHolidayDays,
  expireOldHolidayDays,
  exportExpireOldHolidayDays,
  getSecuritySettings,
  updateSecuritySettings,
};

export default AdminService;
